/**
 * Booking API — same host, path, and Bearer resolution as `C:\\Users\\donny\\testings\\booking\\*.js`
 * (e.g. `all bookings.js`: GET `{origin}/bookings` with `Authorization: Bearer <token>` only).
 */
import { requestAssetApi, type RequestAssetApiDebugSnapshot } from './getAssetById';

export type BookingApiCallOptions = {
  onRequestDebug?: (snapshot: RequestAssetApiDebugSnapshot) => void;
};

/** Extend end date and change add-ons via PATCH `/bookings/:id`. */
export const BOOKING_GENERAL_UPDATE_API_AVAILABLE = true;

export async function fetchBookingById(
  bookingId: string,
  opts?: BookingApiCallOptions,
): Promise<unknown> {
  const t = bookingId.trim();
  if (!t) {
    throw new Error('Booking ID is required.');
  }
  return requestAssetApi({
    method: 'GET',
    path: `/bookings/${encodeURIComponent(t)}`,
    onRequestDebug: opts?.onRequestDebug,
  });
}

export async function fetchBookingsList(opts?: BookingApiCallOptions): Promise<unknown> {
  /** Trailing slash avoids nginx 301 → cross-origin redirect that breaks browser fetch via dev proxy. */
  return requestAssetApi({ method: 'GET', path: '/bookings/', onRequestDebug: opts?.onRequestDebug });
}

export async function createBooking(body: unknown, opts?: BookingApiCallOptions): Promise<unknown> {
  return requestAssetApi({
    method: 'POST',
    path: '/bookings/create',
    body,
    onRequestDebug: opts?.onRequestDebug,
  });
}

export async function updateBooking(
  bookingId: string,
  body: unknown,
  opts?: BookingApiCallOptions,
): Promise<unknown> {
  const t = bookingId.trim();
  if (!t) {
    throw new Error('Booking ID is required.');
  }
  return requestAssetApi({
    method: 'PATCH',
    path: `/bookings/${encodeURIComponent(t)}`,
    body,
    onRequestDebug: opts?.onRequestDebug,
  });
}

export async function cancelBookingAsClient(
  bookingId: string,
  opts?: BookingApiCallOptions,
): Promise<unknown> {
  const t = bookingId.trim();
  if (!t) {
    throw new Error('Booking ID is required.');
  }
  return requestAssetApi({
    method: 'PATCH',
    path: `/bookings/cancel/client/${encodeURIComponent(t)}`,
    body: {},
    onRequestDebug: opts?.onRequestDebug,
  });
}

export async function cancelBookingAsOwner(
  bookingId: string,
  answer: boolean,
  opts?: BookingApiCallOptions,
): Promise<unknown> {
  const t = bookingId.trim();
  if (!t) {
    throw new Error('Booking ID is required.');
  }
  return requestAssetApi({
    method: 'PATCH',
    path: `/bookings/cancel/owner/${encodeURIComponent(t)}`,
    body: { answer },
    onRequestDebug: opts?.onRequestDebug,
  });
}

export async function modifyBookingPrice(
  bookingId: string,
  totalPrice: number,
  opts?: BookingApiCallOptions,
): Promise<unknown> {
  const t = bookingId.trim();
  if (!t) {
    throw new Error('Booking ID is required.');
  }
  if (!Number.isFinite(totalPrice)) {
    throw new Error('Total price must be a valid number.');
  }
  return requestAssetApi({
    method: 'POST',
    path: '/bookings/modifyprice',
    body: { booking_id: t, total_price: totalPrice },
    onRequestDebug: opts?.onRequestDebug,
  });
}

export async function escalateBooking(bookingId: string, opts?: BookingApiCallOptions): Promise<unknown> {
  const t = bookingId.trim();
  if (!t) {
    throw new Error('Booking ID is required.');
  }
  return requestAssetApi({
    method: 'POST',
    path: `/bookings/escalate/${encodeURIComponent(t)}`,
    body: {},
    onRequestDebug: opts?.onRequestDebug,
  });
}
